import { motion, useScroll, useTransform } from "framer-motion";
import { useEffect, useRef, useState } from "react";
import { ArrowDown, Paintbrush } from "lucide-react";

const Line = ({ children, delay }) => (
    <span className="block overflow-hidden pb-1">
        <motion.span
            className="block"
            initial={{ y: "115%" }}
            animate={{ y: 0 }}
            transition={{ duration: 1, delay, ease: [0.16, 1, 0.3, 1] }}
        >
            {children}
        </motion.span>
    </span>
);

const SALE_END = new Date("2026-10-30T23:59:59").getTime();

const useCountdown = () => {
    const [now, setNow] = useState(Date.now());
    useEffect(() => {
        const t = setInterval(() => setNow(Date.now()), 1000);
        return () => clearInterval(t);
    }, []);
    const diff = Math.max(0, SALE_END - now);
    return {
        d: Math.floor(diff / 86400000),
        h: Math.floor(diff / 3600000) % 24,
        m: Math.floor(diff / 60000) % 60,
        s: Math.floor(diff / 1000) % 60,
    };
};

const scrollTo = (id) => {
    if (window.__lenis) window.__lenis.scrollTo(id, { offset: -70 });
    else document.querySelector(id)?.scrollIntoView({ behavior: "smooth" });
};

export const Hero = ({ product }) => {
    const ref = useRef(null);
    const t = useCountdown();
    const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
    const y = useTransform(scrollYProgress, [0, 1], [0, 140]);
    const rotate = useTransform(scrollYProgress, [0, 1], [0, -6]);

    return (
        <section ref={ref} data-testid="hero-section" className="relative flex min-h-screen items-center overflow-hidden pt-24">
            <div className="spray-glow pointer-events-none absolute -right-40 top-1/4 h-[700px] w-[700px]" />
            <div className="mx-auto grid w-full max-w-7xl items-center gap-12 px-6 lg:grid-cols-[1.2fr_1fr]">
                <div className="relative z-10">
                    <motion.p
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 0.2, duration: 0.8 }}
                        className="mb-6 flex items-center gap-3 font-mono text-xs tracking-[0.3em] text-vault-red"
                    >
                        <Paintbrush size={14} /> CUSTOM SNEAKER STUDIO — EST. IN THE STREETS
                    </motion.p>
                    <h1 className="font-display text-6xl uppercase leading-[0.95] tracking-tight text-stone-900 sm:text-7xl lg:text-8xl">
                        <Line delay={0.35}>Sprayed.</Line>
                        <Line delay={0.5}>
                            <span className="text-vault-red">By hand.</span>
                        </Line>
                        <Line delay={0.65}>Worn loud.</Line>
                    </h1>
                    <motion.p
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.9, duration: 0.8 }}
                        className="mt-8 max-w-md text-base text-stone-600"
                    >
                        Vault Deportivos turns blank sneakers into one-of-one street art. Every pair is masked, sprayed
                        and sealed by hand — no prints, no replicas, no second chances.
                    </motion.p>
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 1.05, duration: 0.8 }}
                        className="mt-10 flex flex-wrap items-center gap-4"
                    >
                        <button
                            data-testid="hero-shop-button"
                            onClick={() => scrollTo("#drop")}
                            className="btn-sweep border border-vault-red px-8 py-4 font-mono text-sm tracking-[0.2em] text-stone-900"
                        >
                            SHOP DROP 001
                        </button>
                        <button
                            data-testid="hero-custom-button"
                            onClick={() => scrollTo("#custom")}
                            className="border border-vault-border px-8 py-4 font-mono text-sm tracking-[0.2em] text-stone-600 transition-colors hover:border-white hover:text-stone-900"
                        >
                            CUSTOM ORDER
                        </button>
                    </motion.div>
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 1.2, duration: 0.8 }}
                        data-testid="sale-countdown"
                        className="mt-10"
                    >
                        <p className="font-mono text-[10px] tracking-[0.3em] text-vault-red">
                            DURGA PUJA SALE — 50% OFF EVERYTHING — ENDS 30.10.2026
                        </p>
                        <div className="mt-3 flex gap-2">
                            {[
                                [t.d, "DAYS"],
                                [t.h, "HRS"],
                                [t.m, "MIN"],
                                [t.s, "SEC"],
                            ].map(([v, l]) => (
                                <div key={l} className="border border-vault-border bg-white px-4 py-3 text-center">
                                    <p
                                        data-testid={`countdown-${l.toLowerCase()}`}
                                        className="font-mono text-2xl tabular-nums text-stone-900"
                                    >
                                        {String(v).padStart(2, "0")}
                                    </p>
                                    <p className="mt-1 font-mono text-[9px] tracking-[0.25em] text-stone-400">{l}</p>
                                </div>
                            ))}
                        </div>
                    </motion.div>
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 1.4 }}
                        className="mt-14 flex items-center gap-2 font-mono text-[10px] tracking-[0.3em] text-stone-400"
                    >
                        <ArrowDown size={12} className="animate-bounce" /> SCROLL
                    </motion.div>
                </div>
                <motion.div style={{ y, rotate }} className="relative hidden lg:block">
                    <motion.div
                        initial={{ opacity: 0, scale: 0.92 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ delay: 0.6, duration: 1.1, ease: [0.16, 1, 0.3, 1] }}
                        className="clip-frame relative border border-vault-border bg-white p-6"
                    >
                        <img
                            data-testid="hero-product-image"
                            src={product.image}
                            alt={product.name}
                            className="w-full object-contain"
                        />
                        <div className="absolute bottom-4 left-4 bg-vault-black px-3 py-2 font-mono text-[10px] tracking-[0.25em] text-vault-red">
                            DROP 001 — FIRST DESIGN
                        </div>
                    </motion.div>
                </motion.div>
            </div>
        </section>
    );
};
